import { Component, OnInit, inject, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { FormTemplateService } from 'src/app/Services/Forms/form-template.service';
import { UserDataService } from 'src/app/Services/Authentication/User/user-data.service';
import { Form_Template, Response_Forms } from 'src/app/Interfaces/Forms/FormTemplate.interface';
import { GeneralRoleCDMS, typeSystemRoleCDMS } from 'src/app/types/General/RoleCDMS.type';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-form-templates-cdms',
  templateUrl: './form-templates-cdms.component.html',
  styleUrls: ['./form-templates-cdms.component.scss', '../../../Styles/GlobalStyles.css'],
  standalone: false
})
export class FormTemplatesCDMSComponent implements OnInit {

  public FormTemplateService = inject(FormTemplateService);
  public UserService = inject(UserDataService);
  private router = inject(Router);

  public RolesGeneral: typeSystemRoleCDMS = GeneralRoleCDMS;

  public Templates: Form_Template[] = [];
  public FilteredTemplates: Form_Template[] = [];
  public SearchText: string = '';
  public SelectedStatus: number | null = null;
  public SelectedCategory: string = '';
  public activeMenuId: number | null = null;

  public StatusOptions = [
    { label: 'Todos', value: null },
    { label: 'Borrador', value: 1 },
    { label: 'Activo', value: 2 },
    { label: 'Archivado', value: 3 }
  ];

  public SortOptions = [
    { label: 'Más reciente', value: 'newest' },
    { label: 'Nombre A-Z', value: 'name_asc' },
    { label: 'Nombre Z-A', value: 'name_desc' }
  ];
  public SelectedSort: string = 'newest';

  private ColorConfirm: string = '#20a8e1';
  private ColorsCancel: string = '#fc6161';

  constructor() {}

  public CanManageFormTemplates(): boolean {
    return this.UserService.UserRoleGeneralIn([
      this.RolesGeneral.FormTemplatesAdmin,
      this.RolesGeneral.FormTemplatesUser,
      this.RolesGeneral.AdministratorFIN
    ]);
  }

  ngOnInit(): void {
    this.LoadTemplates();
  }

  public LoadTemplates(): void {
    this.FormTemplateService.GetAllFormTemplates(
      this.SelectedStatus || undefined,
      this.SelectedCategory || undefined
    ).subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          this.Templates = res.operation;
          this.ApplyFilters();
        }
      },
      error: (err) => {
        console.error('Error loading templates', err);
      }
    });
  }

  public ApplyFilters(): void {
    let filtered = [...this.Templates];

    // When no specific status filter is selected ("Todos"), hide archived by default
    if (this.SelectedStatus === null) {
      filtered = filtered.filter(t => t.Status !== 3);
    }

    if (this.SearchText.trim()) {
      const search = this.SearchText.toLowerCase();
      filtered = filtered.filter(t =>
        t.Name.toLowerCase().includes(search) ||
        (t.Category && t.Category.toLowerCase().includes(search))
      );
    }

    switch (this.SelectedSort) {
      case 'name_asc':
        filtered.sort((a, b) => a.Name.localeCompare(b.Name));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.Name.localeCompare(a.Name));
        break;
      case 'newest':
      default:
        filtered.sort((a, b) => new Date(b.Date_Created).getTime() - new Date(a.Date_Created).getTime());
        break;
    }

    this.FilteredTemplates = filtered;
  }

  public OnStatusChange(): void {
    this.LoadTemplates();
  }

  public GetStatusLabel(status: number): string {
    switch (status) {
      case 1: return 'Borrador';
      case 2: return 'Activo';
      case 3: return 'Archivado';
      default: return '';
    }
  }

  public GetStatusClass(status: number): string {
    switch (status) {
      case 1: return 'status-draft';
      case 2: return 'status-active';
      case 3: return 'status-archived';
      default: return '';
    }
  }

  public GetTemplateCountByStatus(status: number): number {
    return this.Templates.filter(t => t.Status === status).length;
  }

  public GetTimeAgo(date: string): string {
    const now = new Date();
    const d = new Date(date);
    const diff = now.getTime() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Justo ahora';
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days === 1) return 'Ayer';
    if (days < 30) return `Hace ${days} días`;
    return d.toLocaleDateString('es-MX');
  }

  public NavigateToGenAI(id?: number): void {
    if (id) {
      this.router.navigate(['/Dashboard/General/FormGenAI', id]);
    } else {
      this.router.navigate(['/Dashboard/General/FormGenAI']);
    }
  }

  public NavigateToBuilder(id?: number): void {
    if (id) {
      this.router.navigate(['/Dashboard/General/FormBuilder', id]);
    } else {
      this.router.navigate(['/Dashboard/General/FormBuilder']);
    }
  }

  public DuplicateTemplate(template: Form_Template): void {
    Swal.fire({
      title: '¿Duplicar formulario?',
      text: `Se creará una copia de "${template.Name}"`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: this.ColorConfirm,
      cancelButtonColor: this.ColorsCancel,
      confirmButtonText: 'Sí, duplicar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.FormTemplateService.DuplicateFormTemplate(template.ID).subscribe({
          next: (res: Response_Forms) => {
            if (res.success) {
              Swal.fire('Duplicado', 'El formulario se ha duplicado correctamente.', 'success');
              this.LoadTemplates();
            }
          },
          error: () => {
            Swal.fire('Error', 'No se pudo duplicar el formulario.', 'error');
          }
        });
      }
    });
  }

  public ArchiveTemplate(template: Form_Template): void {
    Swal.fire({
      title: '¿Archivar formulario?',
      text: `El formulario "${template.Name}" se archivará`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: this.ColorConfirm,
      cancelButtonColor: this.ColorsCancel,
      confirmButtonText: 'Sí, archivar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.FormTemplateService.ChangeStatusFormTemplate(template.ID, 3).subscribe({
          next: (res: Response_Forms) => {
            if (res.success) {
              Swal.fire('Archivado', 'El formulario se ha archivado.', 'success');
              this.LoadTemplates();
            }
          }
        });
      }
    });
  }

  public UnarchiveTemplate(template: Form_Template): void {
    Swal.fire({
      title: '¿Restaurar formulario?',
      text: `"${template.Name}" volverá a estar activo`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: this.ColorConfirm,
      cancelButtonColor: this.ColorsCancel,
      confirmButtonText: 'Sí, restaurar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.FormTemplateService.ChangeStatusFormTemplate(template.ID, 2).subscribe({
          next: (res: Response_Forms) => {
            if (res.success) {
              Swal.fire('Restaurado', 'El formulario está activo nuevamente.', 'success');
              this.LoadTemplates();
            }
          },
          error: () => {
            Swal.fire('Error', 'No se pudo restaurar el formulario.', 'error');
          }
        });
      }
    });
  }

  public DeleteTemplate(template: Form_Template): void {
    Swal.fire({
      title: '¿Eliminar formulario?',
      text: `Esta acción no se puede deshacer`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: this.ColorConfirm,
      cancelButtonColor: this.ColorsCancel,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.FormTemplateService.ChangeStatusFormTemplate(template.ID, 4).subscribe({
          next: (res: Response_Forms) => {
            if (res.success) {
              Swal.fire('Eliminado', 'El formulario se ha eliminado.', 'success');
              this.LoadTemplates();
            }
          }
        });
      }
    });
  }

  public ToggleMenu(id: number, event: Event): void {
    event.stopPropagation();
    if (this.activeMenuId === id) {
      this.activeMenuId = null;
    } else {
      this.activeMenuId = id;
    }
  }

  @HostListener('document:click', ['$event'])
  public OnClickOutside(event: MouseEvent): void {
    this.activeMenuId = null;
  }
}
